import React, { createContext, useContext, useMemo, useState } from 'react';

import { SimpleTooltip, Switch } from '@eluve/components';
import { useSession } from '@eluve/session-helpers';
import { EluveAdminOnly } from '@eluve/smart-blocks';

import { EluveAdminPanel } from './components/EluveAdminPanel/EluveAdminPanel';

interface EluveAdminControlsContextValue {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

const EluveAdminControlsContext =
  createContext<EluveAdminControlsContextValue | null>(null);

const useEluveAdminControlsContext = (): EluveAdminControlsContextValue => {
  const ctx = useContext(EluveAdminControlsContext);
  if (!ctx) {
    throw new Error(
      'EluveAdminControls compound components must be used inside <EluveAdminControlsProvider>',
    );
  }
  return ctx;
};

export const EluveAdminControlsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const value = useMemo(() => ({ enabled, setEnabled }), [enabled]);
  return (
    <EluveAdminControlsContext.Provider value={value}>
      {children}
    </EluveAdminControlsContext.Provider>
  );
};

export const EluveAdminControlsToggle: React.FC = () => {
  const { enabled, setEnabled } = useEluveAdminControlsContext();
  return (
    <EluveAdminOnly>
      <SimpleTooltip label="Admin controls">
        <div>
          <Switch
            aria-label="Admin controls"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>
      </SimpleTooltip>
    </EluveAdminOnly>
  );
};

interface EluveAdminControlsPanelProps {
  readonlyMode?: boolean;
}

export const EluveAdminControlsPanel: React.FC<
  EluveAdminControlsPanelProps
> = ({ readonlyMode }) => {
  const { isEluveAdmin } = useSession();
  const { enabled } = useEluveAdminControlsContext();

  if (!isEluveAdmin || !enabled) {
    return null;
  }

  return <EluveAdminPanel readonlyMode={readonlyMode} />;
};
